import React, { useId } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material';
import { CloseOutlined, InfoOutlined } from '@mui/icons-material';
import Tooltip from '../LibraryTooltip';
import { assistantPrimaryButtonSx, assistantTextButtonSx } from '../../freestyle/assistantButtonStyles';

export default function RuntimeInputsDialog({ open, editing = false, capability, variables = [], values = {}, onChange, onClose, onConfirm, title, confirmLabel, showCancel = true, autoFocus = false }) {
  const id = useId();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={`${id}-title`}
      PaperProps={{ sx: { borderRadius: '8px', m: 2, width: 'calc(100% - 32px)', maxHeight: 'calc(100dvh - 32px)' } }}>
      <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1.5, pr: 7, fontSize: '1rem', fontWeight: 600 }}>
        <Box component="span" id={`${id}-title`}>{title || (editing ? 'Session inputs' : 'Review inputs')}</Box>
        <IconButton aria-label="Close inputs" onClick={onClose} size="small" sx={{ position: 'absolute', right: 16, top: 16 }}><CloseOutlined fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 2 }}>
        {capability && <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, overflowWrap: 'anywhere' }}>{capability.title || capability.name || 'Draft capability'}</Typography>}
        <Stack spacing={2.5}>
          {variables.map((variable, index) => {
            const label = variable.name.replace(/_/g, ' ');
            const defaultValue = String(variable.defaultValue ?? '');
            const displayLabel = label.charAt(0).toUpperCase() + label.slice(1);
            const help = [variable.description, defaultValue ? `Default: ${defaultValue}` : ''].filter(Boolean).join('\n\n');
            return <Box key={variable.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <Typography component="label" htmlFor={`${id}-input-${index}`} sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                  {displayLabel}
                </Typography>
                {help && <Tooltip title={help} describeChild placement="top" arrow>
                  <IconButton size="small" aria-label={`About ${displayLabel.toLowerCase()}`} sx={{ p: 0.25, color: 'text.secondary' }}>
                    <InfoOutlined sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>}
              </Box>
              <TextField id={`${id}-input-${index}`} autoFocus={autoFocus && index === 0} value={values[variable.name] ?? ''} onChange={(event) => onChange(variable.name, event.target.value)}
                fullWidth multiline minRows={1} maxRows={8} size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.875rem', lineHeight: 1.6 } }} />
            </Box>;
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 0.5 }}>
        {showCancel && <Button onClick={onClose} sx={assistantTextButtonSx}>Cancel</Button>}
        <Button variant="contained" onClick={onConfirm} sx={assistantPrimaryButtonSx}>{confirmLabel || (editing ? 'Apply inputs' : 'Continue')}</Button>
      </DialogActions>
    </Dialog>
  );
}
